import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Input, Typography, Tag, Spin, Empty, Segmented, Space, Badge, Checkbox, Button, Tooltip, message, Table, Popover } from 'antd';
import {
    SearchOutlined,
    FireOutlined,
    UserOutlined,
    AppstoreOutlined,
    TeamOutlined,
    LinkOutlined,
    BlockOutlined,
    PlusOutlined,
    ImportOutlined,
    UserAddOutlined,
    MailOutlined,
    SolutionOutlined,
    CrownOutlined,
    TableOutlined,
    CalendarOutlined,
    SafetyCertificateOutlined,
    DeleteOutlined,
    DownloadOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EntityImage } from '../Entities/EntityImage';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';
import { useSuperAdmins } from '../../hooks/use_super_admins';
import { useAdminPeople } from '../../hooks/use_admin_people';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useUnclaimedPeople } from '../../hooks/use_unclaimed_people';
import { usePendingRegistrations } from '../../hooks/use_pending_registrations';
import { usePersonFields } from '../../hooks/use_person_fields';
import { buildContactsCsv, downloadCsv } from '../../utils/contacts_csv';
import { buildBrowseQuery, parseBrowseQuery, evaluateBrowseFilter, hasFilterClauses, isQueryExpression } from '../../utils/browse_query';
import CreateEntityModal from '../Entities/CreateEntityModal';
import CSVImportModal from '../Entities/CSVImportModal';
import ExportCsvModal from './ExportCsvModal';
import BulkDeleteModal from './BulkDeleteModal';
import { TABLE_COMPONENTS, compareNullsLast, textSearchDropdown } from './browse_table_helpers';
import BrowseCalendar from './BrowseCalendar';
import InvitePeopleModal from '../Entities/InvitePeopleModal';
import './Homepage.css';

const { Text } = Typography;

// Duplicated in Entities/EntityAvatar.jsx — keep both in sync.
const ENTITY_TYPE_COLORS = {
    context: '#52c41a',
    app: '#722ed1',
    relationship: '#faad14',
    person: '#1677ff',
};

// Per-viewer table column visibility: { [columnKey]: boolean } overrides on
// top of each column's default. Browser-local convenience, like the theme.
const COLUMN_PREFS_KEY = 'browse.tableColumns';

function loadColumnPrefs() {
    try {
        return JSON.parse(localStorage.getItem(COLUMN_PREFS_KEY)) || {};
    } catch {
        return {};
    }
}

// Per-viewer column widths from dragging a header edge: { [columnKey]: px }.
// Same browser-local convenience as the visibility prefs above; a column with
// no entry keeps its default width.
const COLUMN_WIDTHS_KEY = 'browse.tableColumnWidths';

function loadColumnWidths() {
    try {
        return JSON.parse(localStorage.getItem(COLUMN_WIDTHS_KEY)) || {};
    } catch {
        return {};
    }
}

function saveColumnWidths(widths) {
    try { localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(widths)); } catch { /* storage blocked — keep in memory */ }
}

// Per-viewer choice of table or calendar. Browser-local like the column prefs:
// a view is how you like to look at the rows, not part of a query, so it stays
// out of ?q= and off the shareable link.
const VIEW_MODE_KEY = 'browse.viewMode';
const VIEW_MODES = ['table', 'calendar'];

function loadViewMode() {
    try {
        const saved = localStorage.getItem(VIEW_MODE_KEY);
        return VIEW_MODES.includes(saved) ? saved : 'table';
    } catch {
        return 'table';
    }
}

const placeLabel = p => (p && typeof p === 'object' && p.lat != null && p.lng != null
    ? (p.label || `${p.lat}, ${p.lng}`)
    : null);

// A custom field value as display strings: one per place for geo_multi, at
// most one otherwise. The table filters on these individually, so a
// multi-location row matches a filter on any one of its places.
function personFieldTokens(field, raw) {
    if (raw === undefined || raw === null || raw === '') return [];
    if (field.field_type === 'date') return [new Date(raw).toLocaleDateString()];
    if (field.field_type === 'geo') return [placeLabel(raw)].filter(Boolean);
    if (field.field_type === 'geo_multi') return (Array.isArray(raw) ? raw : []).map(placeLabel).filter(Boolean);
    return [String(raw)];
}

function formatPersonFieldValue(field, raw) {
    const tokens = personFieldTokens(field, raw);
    return tokens.length ? tokens.join('; ') : null;
}

// Sort key for a custom field value: numbers/dates compare numerically,
// everything else as text. Missing values sort as null (always last).
function personFieldSortKey(field, raw) {
    if (raw === undefined || raw === null || raw === '') return null;
    if (field.field_type === 'number') return Number(raw);
    if (field.field_type === 'date') return new Date(raw).getTime();
    // Dropdowns sort in the admin's option order (e.g. terms oldest-first);
    // a value whose option was since removed sorts after the listed ones.
    if (field.field_type === 'select') {
        const i = (field.options || []).indexOf(raw);
        return i === -1 ? (field.options || []).length : i;
    }
    if (field.field_type === 'geo' || field.field_type === 'geo_multi') return formatPersonFieldValue(field, raw);
    return String(raw);
}

// Same free-text dropdown as textSearchDropdown, plus a spinner — used by the
// Description column, whose clause is answered by the hybrid search behind the
// main search bar instead of a local "contains" match. The clause commits on
// every keystroke as usual; the search itself is what waits for a pause.
function hybridSearchDropdown(placeholder, searching) {
    return ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
            <Input
                autoFocus
                allowClear
                placeholder={placeholder}
                value={selectedKeys[0]}
                suffix={searching ? <Spin size="small" /> : null}
                onChange={e => {
                    setSelectedKeys(e.target.value ? [e.target.value] : []);
                    confirm({ closeDropdown: false });
                }}
                onPressEnter={() => confirm()}
                style={{ width: 200, marginBottom: 8, display: 'block' }}
            />
            <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>Reset</Button>
        </div>
    );
}

// Shown under the query bar while it has focus. Each row is one thing you can
// write, in the order you'd reach for them — plain words first, since that's
// what most people want and it needs no syntax at all. `equals` is filled in
// from a column this node actually has (see queryHelp), because person fields
// differ per node and a hardcoded example could name a column that isn't here.
const queryHelpRows = (equals) => [
    ['community gardening', 'Plain words search both ways at once — they expand to the two clauses below, OR\'d.'],
    ['Description means "biking"', 'Embedding search — matched on meaning and on exact wording, across name and description.'],
    ['Name contains "ali"', 'Literal substring, case-insensitive.'],
    [equals, 'One exact value, chosen from that column\'s own options.'],
    ['Type = "People" OR "Contexts"', 'Several values from one column.'],
    ['… AND …', 'Both must hold. This is what the column filters build.'],
    ['… OR …', 'Either may hold — the one thing a column filter can\'t express.'],
    ['ORDER BY Name ASC', 'Sort. DESC to reverse; comma-separate to break ties.'],
    ['SHOW "Name", "Description"', 'Choose which columns are on screen.'],
];

const TYPE_FILTERS = [
    { value: 'all', label: 'All', icon: <AppstoreOutlined /> },
    { value: 'person', label: 'People', icon: <UserOutlined /> },
    { value: 'context', label: 'Contexts', icon: <TeamOutlined /> },
    { value: 'app', label: 'Apps', icon: <BlockOutlined /> },
    { value: 'relationship', label: 'Relationships', icon: <LinkOutlined /> },
];

function EntityCard({ entity, onClick, unclaimed, unlisted, showTypeIdentifiers }) {
    const color = showTypeIdentifiers ? (ENTITY_TYPE_COLORS[entity.entity_type] || '#8c8c8c') : 'var(--border-color)';
    return (
        <Card
            size="small"
            hoverable
            onClick={onClick}
            style={{
                width: 260,
                maxWidth: '100%',
                borderRadius: 8,
                background: 'var(--bg-secondary)',
                cursor: 'pointer',
                borderTop: `3px solid ${color}`,
            }}
            styles={{ body: { padding: '12px 14px' } }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                {/* Same as the table's Name column: the picture from row data,
                    no type chip — the card's type Tag and border carry that. */}
                <EntityImage entity={entity} size={24} />
                <Text strong ellipsis style={{ flex: 1, fontSize: 13 }}>{entity.name}</Text>
                {entity.hangout_active && (
                    <Badge status="processing" color="#ff4d4f" />
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <Space size={4}>
                    {showTypeIdentifiers && (
                        <Tag style={{ fontSize: 10, margin: 0, textTransform: 'capitalize' }}>{entity.entity_type}</Tag>
                    )}
                    {unclaimed && (
                        <Tooltip title="No linked account yet — visible only to superadmins">
                            <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>Pending</Tag>
                        </Tooltip>
                    )}
                    {unlisted && !unclaimed && (
                        <Tooltip title="Not discoverable in browse — visible only to superadmins">
                            <Tag style={{ fontSize: 10, margin: 0 }}>Unlisted</Tag>
                        </Tooltip>
                    )}
                </Space>
                {entity.description && (
                    <Text type="secondary" ellipsis style={{ fontSize: 11, flex: 1, textAlign: 'right' }}>
                        {entity.description}
                    </Text>
                )}
            </div>
        </Card>
    );
}

/**
 * Browse-grid card for someone who has registered but not been approved yet (#35).
 *
 * Deliberately not an EntityCard: a registration is a user profile
 * ({ id: keycloak uuid, name, email }), not an entity. There may be no person
 * entity behind it yet and its id is a uuid, not an entity id — so this card
 * carries the approve/reject decision instead of navigating anywhere.
 */
function RegistrationCard({ registration, busy, onApprove, onReject }) {
    return (
        <Card
            size="small"
            style={{
                width: 260,
                maxWidth: '100%',
                borderRadius: 8,
                background: 'var(--bg-secondary)',
                borderTop: `3px solid ${ENTITY_TYPE_COLORS.person}`,
            }}
            styles={{ body: { padding: '12px 14px' } }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Text strong ellipsis style={{ flex: 1, fontSize: 13 }}>
                    {registration.name || '(no name)'}
                </Text>
                <Tooltip title="Registered but not approved — visible only to superadmins">
                    <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>Unapproved</Tag>
                </Tooltip>
            </div>
            {registration.email && (
                <Text type="secondary" ellipsis style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
                    {registration.email}
                </Text>
            )}
            <Space size={4}>
                <Button size="small" type="primary" loading={busy} onClick={onApprove}>Approve</Button>
                <Button size="small" danger loading={busy} onClick={onReject}>Reject</Button>
            </Space>
        </Card>
    );
}

function Homepage() {
    const navigate = useNavigate();
    const api = useApi();
    const { user } = useAuth();
    const {
        isServerAdmin, admins: superAdminSet,
        add: addSuperAdmin, remove: removeSuperAdmin, refresh: refreshSuperAdmins,
    } = useSuperAdmins();
    const { enabledEntityTypes, featureEntityTypeIdentifiers, featureEntityListing, featureRegistrationApproval } = useNodeInfo();
    // Superadmin-only: every person on the node, including the unlisted ones the
    // plain /api/entities?listed=true fetch below never returns. Merged into the
    // grid so a superadmin sees the full roster — unclaimed people to action from
    // the Pending tab, and unlisted-but-claimed people that listing hides.
    const { allPeople, refresh: refreshUnclaimedPeople } = useUnclaimedPeople();
    // Superadmin-only: accounts awaiting an approval decision. Not entities —
    // they get their own tab and card rather than joining the entity grid.
    const { pending: unapprovedPeople, approve, reject } = usePendingRegistrations();
    // Public: person entities that are node super admins, for the Admins tab.
    const { admins: adminPeople, loading: adminsLoading, refresh: refreshAdminPeople } = useAdminPeople();

    // Tier 3 gate (#36): only offer entity types this node has enabled. null =>
    // not yet known, so show all (the backend still enforces the real gate).
    const typeFilters = useMemo(() => {
        const base = enabledEntityTypes
            ? TYPE_FILTERS.filter(t => t.value === 'all' || enabledEntityTypes.includes(t.value))
            : TYPE_FILTERS;
        const personIdx = base.findIndex(t => t.value === 'person');
        // Admins is public — every user, not just other admins, can see who
        // administers this node — so it's inserted right after People
        // regardless of isServerAdmin.
        const withAdmins = personIdx === -1
            ? base
            : [...base.slice(0, personIdx + 1), { value: 'admins', label: 'Admins', icon: <CrownOutlined /> }, ...base.slice(personIdx + 1)];
        if (!isServerAdmin) return withAdmins;
        // Both are superadmin-only lists of people in a waiting state, so keep
        // them together right after Admins: Pending = person entities nobody has
        // claimed, Unapproved = accounts nobody has let in yet. Unapproved only
        // exists on a node with the approval step (feature_registration_approval);
        // without it signups go straight in and there's nothing to wait on.
        const adminTabs = [
            { value: 'unclaimed', label: 'Pending', icon: <MailOutlined /> },
            featureRegistrationApproval && { value: 'unapproved', label: 'Unapproved', icon: <SolutionOutlined /> },
        ].filter(Boolean);
        const anchorIdx = withAdmins.findIndex(t => t.value === 'admins');
        if (anchorIdx === -1) return [...withAdmins, ...adminTabs];
        return [...withAdmins.slice(0, anchorIdx + 1), ...adminTabs, ...withAdmins.slice(anchorIdx + 1)];
    }, [enabledEntityTypes, isServerAdmin, featureRegistrationApproval]);

    // On a person-only node the only thing Add Entity could create is a person,
    // which Invite People already does in fewer steps (and mints the claim link
    // too) — so the generic create flow is hidden there. null => not yet known,
    // so keep it, same permissive convention as typeFilters above.
    const personOnlyNode = !!enabledEntityTypes && enabledEntityTypes.every(t => t === 'person');

    const [registrationBusy, setRegistrationBusy] = useState(null); // uuid mutating

    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [typeFilter, setTypeFilter] = useState('all');
    // The flag is permissive until /info lands, so the Unapproved tab can be
    // selected (from a shared link, say) a moment before it turns out not to
    // exist on this node — fall back to All rather than an empty, tabless view.
    useEffect(() => {
        if (typeFilter === 'unapproved' && !featureRegistrationApproval) setTypeFilter('all');
    }, [typeFilter, featureRegistrationApproval]);
    // 'table' | 'calendar', remembered per browser. The calendar lays the same
    // filtered rows over a month by their windows (todo D12); the filter bar
    // is shared by both.
    const [viewMode, setViewMode] = useState(loadViewMode);
    const setViewModePersisted = (mode) => {
        setViewMode(mode);
        try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* storage blocked — keep in memory */ }
    };
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [csvImportOpen, setCsvImportOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    // Table-view row selection, for the superadmin bulk action bar (make/remove
    // admin, delete). Cleared on tab change so stale ids from a different tab's
    // rows never linger.
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkActionBusy, setBulkActionBusy] = useState(false);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const personFields = usePersonFields();
    const [columnPrefs, setColumnPrefs] = useState(loadColumnPrefs);
    const [columnWidths, setColumnWidths] = useState(loadColumnWidths);
    // Filters in disjunctive normal form: OR branches, each an AND of per-column
    // clauses. `[{}]` is no filter. Column dropdowns only ever edit branch 0 —
    // a dropdown can't express a relationship with a *different* column — so
    // extra branches arrive only from the query bar or a shared link.
    const [filterGroups, setFilterGroups] = useState([{}]);
    // antd reports the columns it rendered; anything it didn't (a hidden column
    // carrying a clause from a link) is left alone rather than dropped.
    const applyDropdownFilters = (filters) => setFilterGroups(prev => {
        const branch = { ...prev[0] };
        for (const [key, values] of Object.entries(filters)) {
            if (Array.isArray(values) && values.length > 0) branch[key] = values; else delete branch[key];
        }
        return [branch, ...prev.slice(1)];
    });
    const dropFilterColumn = (key) => setFilterGroups(prev => prev.map(({ [key]: _dropped, ...rest }) => rest));
    // The view this page was opened with, if it came from a shared link. Read
    // once at mount (before the sync-out effect can overwrite ?q=) and cleared
    // as soon as it's been applied.
    const [searchParams, setSearchParams] = useSearchParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const [pendingQuery, setPendingQuery] = useState(() => searchParams.get('q'));
    // Sort is controlled for the same reason, and so the banner above the
    // table can read it. { [columnKey]: 'ascend' | 'descend' }.
    const [tableSorters, setTableSorters] = useState({});
    // antd hands `onChange` a single sorter object, or an array once any column
    // declares `sorter: { multiple }`. Normalise both into the keyed map.
    const applySorter = (sorter) => setTableSorters(Object.fromEntries(
        [].concat(sorter)
            .filter(s => s?.order && (s.columnKey ?? s.field))
            .map(s => [s.columnKey ?? s.field, s.order]),
    ));
    // Saved column prefs are a standing personal setting, not part of a query,
    // so they only join the shareable view once touched here (or arrived in a
    // link) — otherwise every visit would carry a SHOW clause it never chose.
    const [columnsTouched, setColumnsTouched] = useState(false);
    const setColumnVisible = (key, visible) => {
        setColumnsTouched(true);
        if (!visible) {
            dropFilterColumn(key);
            setTableSorters(prev => { const { [key]: _dropped, ...rest } = prev; return rest; });
        }
        setColumnPrefsPersisted(key, visible);
    };
    const setColumnPrefsPersisted = (key, visible) => setColumnPrefs(prev => {
        const next = { ...prev, [key]: visible };
        try { localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(next)); } catch { /* storage blocked — keep in memory */ }
        return next;
    });

    // Description clauses are answered by the same /api/entities/search hybrid
    // (vector + text) search as the main search bar, rather than a local
    // "contains" match, so they also catch semantically related descriptions and
    // typos. Results are cached per query string, because an OR expression can
    // ask about several different descriptions at once. A query with no entry
    // yet is permissive, so an in-flight search never blanks the table.
    // Each entry is a Map of entity id -> the hit's scores, so the Similarity
    // column can show how strongly a row matched, not just that it did.
    const [descriptionHits, setDescriptionHits] = useState({});
    const [descriptionSearching, setDescriptionSearching] = useState(false);
    const descriptionsInFlight = useRef(new Set());

    const fetchEntities = useCallback(() => {
        setLoading(true);
        return api.get('/api/entities', { params: { listed: true } })
            .then(data => { setEntities(data); setLoadError(null); })
            .catch(err => { setLoadError(err.message); })
            .finally(() => { setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { fetchEntities(); }, [fetchEntities]);

    // Every distinct description the current expression asks about, from every
    // branch — the column dropdown, the main search bar and a shared link all
    // reach the search through this one path.
    const descriptionQueries = useMemo(
        () => [...new Set(filterGroups.flatMap(g => g.description ?? []))].filter(q => q.trim().length >= 2),
        [filterGroups],
    );

    useEffect(() => {
        const todo = descriptionQueries.filter(q => descriptionHits[q] === undefined && !descriptionsInFlight.current.has(q));
        if (todo.length === 0) return undefined;
        let alive = true;
        // Debounced here rather than at the keystroke: every edit re-runs this
        // effect, and the cleanup cancels the run it queued, so a burst of
        // typing costs one request no matter which path set the clause.
        const timer = setTimeout(() => {
            todo.forEach(q => descriptionsInFlight.current.add(q));
            setDescriptionSearching(true);
            Promise.all(todo.map(query => api
                .post('/api/entities/search', { query, limit: 50, vector_weight: 0.5, text_weight: 0.5 })
                .then(data => [query, new Map(data.map(r => [r.id, { score: r.score, vector: r.vector_score, text: r.text_score }]))])
                // A failed search matches nothing rather than everything — better
                // an empty table the user can see is wrong than silent noise.
                .catch(() => [query, new Map()])))
                .then(pairs => {
                    todo.forEach(q => descriptionsInFlight.current.delete(q));
                    if (!alive) return;
                    setDescriptionHits(prev => ({ ...prev, ...Object.fromEntries(pairs) }));
                    setDescriptionSearching(descriptionsInFlight.current.size > 0);
                });
        }, 350);
        return () => { alive = false; clearTimeout(timer); };
    }, [descriptionQueries, descriptionHits, api]);

    // A row's best hit across every Description query in the expression — an
    // OR can ask about several, and the strongest one is what put it on screen.
    // null for a row no finished search returned (its query is still in flight).
    const similarityFor = useCallback((entity) => descriptionQueries.reduce((best, q) => {
        const hit = descriptionHits[q]?.get(entity.id);
        return hit && (!best || hit.score > best.score) ? hit : best;
    }, null), [descriptionQueries, descriptionHits]);

    const handleRegistration = (userId, action, fn) => {
        setRegistrationBusy(userId);
        fn(userId)
            .then(() => message.success(`Registration ${action}`))
            .catch(() => message.error(`Could not ${action} this registration`))
            .finally(() => setRegistrationBusy(null));
    };

    // Merge in the superadmin-only people the listed=true fetch missed — unlisted
    // rows of any kind (CSV imports, people who opted out of discovery). Deduped
    // by id, so a person already in the listed set is not added twice.
    const entitiesWithAllPeople = useMemo(() => {
        if (!isServerAdmin || allPeople.length === 0) return entities;
        const existingIds = new Set(entities.map(e => e.id));
        const missing = allPeople.filter(p => !existingIds.has(p.id));
        return missing.length > 0 ? [...entities, ...missing] : entities;
    }, [entities, allPeople, isServerAdmin]);

    const isUnclaimed = useCallback(
        e => isServerAdmin && e.entity_type === 'person' && !e.user_id,
        [isServerAdmin],
    );

    // A person nobody has accepted an invite for yet. Belongs to the superadmin
    // Pending tab only — not People, and not All. Deliberately *not* role-gated
    // like isUnclaimed(): user_id is on the payload for every caller, so these
    // rows are hidden the same way whoever is browsing, rather than appearing
    // for everyone except the admins who can act on them.
    const isPendingPerson = useCallback(
        e => e.entity_type === 'person' && !e.user_id,
        [],
    );

    // Only a superadmin ever sees these rows, so mark why they're here.
    // Gated off => no listed/unlisted concept on this node, and the backend
    // has already stopped filtering browse on it, so every row is just a row:
    // the Unlisted tag and its filter tab would label a distinction that no
    // longer exists.
    const isUnlisted = useCallback(
        e => featureEntityListing && isServerAdmin && e.is_listed === false,
        [isServerAdmin, featureEntityListing],
    );

    const liveEntities = useMemo(
        () => entitiesWithAllPeople.filter(e => e.hangout_active),
        [entitiesWithAllPeople],
    );

    const filteredEntities = useMemo(() => {
        if (typeFilter === 'admins') return [...adminPeople].sort((a, b) => b.id - a.id);
        const filtered = typeFilter === 'all'
            ? entitiesWithAllPeople.filter(e => !isPendingPerson(e))
            : typeFilter === 'unclaimed'
                ? entitiesWithAllPeople.filter(isUnclaimed)
                : typeFilter === 'person'
                    ? entitiesWithAllPeople.filter(e => e.entity_type === 'person' && !isPendingPerson(e))
                    : entitiesWithAllPeople.filter(e => e.entity_type === typeFilter);
        return [...filtered].sort((a, b) => b.id - a.id);
    }, [entitiesWithAllPeople, typeFilter, isUnclaimed, isPendingPerson, adminPeople]);

    // Status as a single value per row, shared by the Status column's
    // filter and sorter (mirrors the tags it renders).
    const entityStatus = useCallback(
        e => (isUnclaimed(e) ? 'pending' : isUnlisted(e) ? 'unlisted' : 'listed'),
        [isUnclaimed, isUnlisted],
    );

    const tableColumns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            filterDropdown: textSearchDropdown('Search name'),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? 'var(--ant-color-primary)' : undefined }} />,
            matches: (values, entity) => values.some(v => (entity.name || '').toLowerCase().includes(String(v).toLowerCase())),
            render: (_, entity) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* The row already carries image_url/avatar, so the picture
                        renders from row data — no per-entity fetch, no type
                        badge or initials: the Type column covers the former. */}
                    <EntityImage entity={entity} size={24} />
                    <Text strong>{entity.name}</Text>
                    {entity.hangout_active && <Badge status="processing" color="#ff4d4f" />}
                </div>
            ),
        },
        featureEntityTypeIdentifiers && {
            title: 'Type',
            dataIndex: 'entity_type',
            key: 'entity_type',
            width: 140,
            defaultHidden: true,
            sorter: (a, b) => a.entity_type.localeCompare(b.entity_type),
            filters: TYPE_FILTERS.filter(t => t.value !== 'all').map(t => ({ text: t.label, value: t.value })),
            matches: (values, entity) => values.includes(entity.entity_type),
            render: (type) => <Tag style={{ textTransform: 'capitalize' }}>{type}</Tag>,
        },
        {
            title: 'Status',
            key: 'status',
            width: 140,
            defaultHidden: true,
            // Pending/Unlisted are superadmin-only distinctions; for everyone
            // else every row is "listed", so filtering would be meaningless.
            ...(isServerAdmin && {
                sorter: (a, b) => entityStatus(a).localeCompare(entityStatus(b)),
                filters: [
                    { text: 'Pending', value: 'pending' },
                    { text: 'Unlisted', value: 'unlisted' },
                    { text: 'Listed', value: 'listed' },
                ],
                matches: (values, entity) => values.includes(entityStatus(entity)),
            }),
            render: (_, entity) => (
                <Space size={4}>
                    {isUnclaimed(entity) && (
                        <Tooltip title="No linked account yet — visible only to superadmins">
                            <Tag color="gold">Pending</Tag>
                        </Tooltip>
                    )}
                    {isUnlisted(entity) && !isUnclaimed(entity) && (
                        <Tooltip title="Not discoverable in browse — visible only to superadmins">
                            <Tag>Unlisted</Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            filterDropdown: hybridSearchDropdown('Search name and description', descriptionSearching),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? 'var(--ant-color-primary)' : undefined }} />,
            // Matches are whatever the hybrid search returned for that query, not
            // a local "contains". A query still in flight has no entry yet and
            // lets every row through, so the table doesn't blank mid-search.
            matches: (values, entity) => values.some(v => descriptionHits[v] === undefined || descriptionHits[v].has(entity.id)),
            // While a Description clause is active, a small tag leads the cell
            // with the score that ranked the hit. It's the combined hybrid
            // score, not capped at 1 — a name match is deliberately scored
            // above the 0..1 vector band (see services/search.py) — so it only
            // compares rows of the same search. Hover splits it into its parts.
            render: (desc, entity) => {
                const hit = similarityFor(entity);
                const part = v => (v == null ? '—' : v.toFixed(2));
                return (
                    <>
                        {hit && (
                            <Tooltip title={`Similarity · meaning ${part(hit.vector)} · wording ${part(hit.text)}`}>
                                <Tag bordered={false} color="blue" style={{ marginInlineEnd: 6, fontSize: 11, lineHeight: '18px', paddingInline: 5 }}>
                                    {hit.score.toFixed(2)}
                                </Tag>
                            </Tooltip>
                        )}
                        <Text type="secondary">{desc || '—'}</Text>
                    </>
                );
            },
        },
        // Custom Person fields (values in entity.attributes), shown by default
        // like Description. Filter options are the distinct values actually
        // present, plus "(empty)".
        ...personFields.map(f => {
            const tokens = entity => (entity.entity_type === 'person'
                ? personFieldTokens(f, entity.attributes?.[f.key])
                : []);
            const display = entity => formatPersonFieldValue(f, entity.entity_type === 'person' ? entity.attributes?.[f.key] : null);
            const values = [...new Set(filteredEntities.flatMap(tokens))]
                .sort((a, b) => (f.field_type === 'select'
                    ? personFieldSortKey(f, a) - personFieldSortKey(f, b) || a.localeCompare(b)
                    : a.localeCompare(b)));
            return {
                title: f.label,
                key: `field:${f.key}`,
                ellipsis: true,
                sorter: (a, b) => compareNullsLast(
                    a.entity_type === 'person' ? personFieldSortKey(f, a.attributes?.[f.key]) : null,
                    b.entity_type === 'person' ? personFieldSortKey(f, b.attributes?.[f.key]) : null,
                ),
                filters: [
                    ...values.map(v => ({ text: v, value: v })),
                    { text: '(empty)', value: '__empty__' },
                ],
                filterSearch: true,
                matches: (values, entity) => {
                    const t = tokens(entity);
                    return values.some(v => (v === '__empty__' ? t.length === 0 : t.includes(v)));
                },
                render: (_, entity) => <Text type="secondary">{display(entity) ?? '—'}</Text>,
            };
        }),
        // `filteredValue` and `sortOrder` are now only about what the header UI
        // shows — filtering itself happens in `visibleEntities`, because antd's
        // per-column onFilter can only ever AND columns together.
    ].filter(Boolean).map(col => ({
        ...col,
        filteredValue: filterGroups[0]?.[col.key] ?? null,
        ...(col.sorter && { sortOrder: tableSorters[col.key] ?? null }),
    })),
    [featureEntityTypeIdentifiers, isServerAdmin, isUnclaimed, isUnlisted, entityStatus, personFields, filteredEntities, filterGroups, tableSorters, descriptionSearching, descriptionHits, similarityFor]);

    // antd is no longer filtering, so do it here: OR across branches, AND within
    // one. An unknown column lets the row through rather than emptying the table.
    const visibleEntities = useMemo(() => {
        const matchers = Object.fromEntries(tableColumns.map(col => [col.key, col.matches]));
        const rows = filteredEntities.filter(entity => evaluateBrowseFilter(
            entity, filterGroups, (key, values, row) => matchers[key]?.(values, row) ?? true,
        ));
        // A Description search ranks its hits, so show them in that order,
        // best first, unless the viewer has picked a sort of their own (which
        // antd then applies on top, via each column's sortOrder). It's the
        // implicit order of a search, not an ORDER BY clause, so it stays out
        // of the query string. Rows with no score yet (search still in flight,
        // or matched only by a non-Description branch of an OR) keep their
        // usual order after the scored ones; Array.sort is stable.
        if (descriptionQueries.length === 0 || Object.keys(tableSorters).length > 0) return rows;
        const score = e => similarityFor(e)?.score ?? null;
        return rows.sort((a, b) => {
            const sa = score(a); const sb = score(b);
            if (sa === null || sb === null) return (sa === null) - (sb === null);
            return sb - sa;
        });
    }, [tableColumns, filteredEntities, filterGroups, descriptionQueries, tableSorters, similarityFor]);

    // Name is always shown — it's what identifies (and opens) the row.
    const isColumnVisible = col => col.key === 'name' || (columnPrefs[col.key] ?? !col.defaultHidden);
    // Widths are layered on here rather than in tableColumns, so a drag only
    // re-lays the table out and never re-runs the filter/query machinery.
    // Dragging updates state on every move; storage is written once, on
    // release. A double-click on the handle drops the column back to default.
    const visibleColumns = useMemo(
        () => tableColumns.filter(isColumnVisible).map(({ defaultHidden, ...col }) => ({
            ...col,
            width: columnWidths[col.key] ?? col.width,
            onHeaderCell: () => ({
                onResize: w => setColumnWidths(prev => ({ ...prev, [col.key]: w })),
                onResizeEnd: w => setColumnWidths(prev => {
                    const next = { ...prev };
                    if (w == null) delete next[col.key]; else next[col.key] = w;
                    saveColumnWidths(next);
                    return next;
                }),
            }),
        })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tableColumns, columnPrefs, columnWidths],
    );

    // The whole view — column filters, sort, the entity-type tab and the visible
    // columns — as one canonical query string. It's both what the banner shows
    // and what rides in ?q=, so the text a user copies is the text that gets
    // parsed back. See utils/browse_query.js for the grammar.
    const tabLabel = typeFilter === 'all' ? null : (typeFilters.find(t => t.value === typeFilter)?.label ?? null);
    // Only pin columns when the viewer has actually moved away from the
    // defaults — otherwise a shared link would needlessly overwrite the
    // recipient's own column layout.
    const visibleTitles = useMemo(() => (
        columnsTouched && tableColumns.some(col => isColumnVisible(col) !== !col.defaultHidden)
            ? tableColumns.filter(isColumnVisible).map(col => col.title)
            : null
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [tableColumns, columnPrefs, columnsTouched]);

    // A tab on its own isn't a query: clicking "People" shouldn't fill the bar
    // with `Tab = "People"` and put it in the URL. The tab joins the query only
    // once there's something for it to scope. `tabInQuery` records whether it
    // did, so editing the text can tell "never shown" from "deleted".
    const [querySummary, tabInQuery] = useMemo(() => {
        const bare = !hasFilterClauses(filterGroups)
            && Object.keys(tableSorters).length === 0
            && !visibleTitles;
        return [
            buildBrowseQuery({
                columns: tableColumns,
                groups: filterGroups,
                sorters: tableSorters,
                tabLabel: bare ? null : tabLabel,
                visibleTitles,
            }),
            !bare && !!tabLabel,
        ];
    }, [tableColumns, filterGroups, tableSorters, tabLabel, visibleTitles]);

    // Mirror the view into ?q= so the address bar is always the shareable link.
    // `replace` because a filter keystroke isn't a navigation — back should leave
    // the page, not walk the filter history.
    useEffect(() => {
        if (pendingQuery !== null) return;
        if (searchParams.get('q') === (querySummary || null)) return;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (querySummary) next.set('q', querySummary); else next.delete('q');
            return next;
        }, { replace: true });
    }, [querySummary, pendingQuery, searchParams, setSearchParams]);

    // ...and apply one on the way in. Columns arrive asynchronously (person
    // fields, the superadmin-only Status column), so a clause we can't place
    // yet may simply be early — wait for the entity fetch to settle before
    // concluding a title genuinely doesn't exist here.
    const applyQuery = useCallback((text) => {
        const parsed = parseBrowseQuery(text, { columns: tableColumns, tabs: typeFilters });
        // The text is the whole state, so a Tab clause that was there and is
        // now gone means the reader deleted it: back to All. One that was never
        // shown (a bare tab isn't written into the query) leaves the tab alone.
        // SHOW is different again — its absence always means "leave columns
        // alone", since resetting them would throw away a saved layout.
        setTypeFilter(parsed.tab ?? (tabInQuery ? 'all' : typeFilter));
        setFilterGroups(parsed.groups);
        setTableSorters(parsed.sorters);
        if (parsed.visibleKeys) {
            // Applied for this visit only — a link someone shared shouldn't
            // permanently rewrite the recipient's saved column preferences.
            const shown = new Set(parsed.visibleKeys);
            setColumnPrefs(Object.fromEntries(tableColumns.map(col => [col.key, shown.has(col.key)])));
            setColumnsTouched(true);
        }
        if (!parsed.visibleKeys && parsed.groups.some(g => g.description)) {
            // Show what a Description clause is matching on, unless the query
            // named the columns itself — then it meant what it said.
            setColumnsTouched(true);
            setColumnPrefs(prev => ({ ...prev, description: true }));
        }
        if (parsed.unresolved.length > 0) {
            message.warning(`Ignored ${parsed.unresolved.join(', ')} — not available on this node.`);
        }
        return parsed;
    }, [tableColumns, typeFilters, tabInQuery, typeFilter]);

    // ...and apply one on the way in. Columns arrive asynchronously (person
    // fields, the superadmin-only Status column), so a clause we can't place
    // yet may simply be early — wait for the entity fetch to settle before
    // concluding a title genuinely doesn't exist here.
    useEffect(() => {
        if (pendingQuery === null) return;
        const early = parseBrowseQuery(pendingQuery, { columns: tableColumns, tabs: typeFilters });
        if (early.unresolved.length > 0 && loading) return;
        applyQuery(pendingQuery);
        setPendingQuery(null);
    }, [pendingQuery, tableColumns, typeFilters, loading, applyQuery]);

    // The bar is editable so a query can be pasted or hand-written — the only
    // way to ask for OR, since a column dropdown can't reach another column.
    // `null` means "not being edited", so the bar shows the live state.
    const [queryDraft, setQueryDraft] = useState(null);
    const [queryFocused, setQueryFocused] = useState(false);
    // Prefer a custom Person field for the "=" example — those are the columns
    // people actually filter on (Location, Team, …) — and take its value from
    // the options the column really offers, so the example is always valid here.
    const queryHelp = useMemo(() => {
        const withOptions = tableColumns.filter(col => col.filters?.some(f => f.value !== '__empty__'));
        const sample = withOptions.find(col => col.key.startsWith('field:'))
            ?? withOptions.find(col => col.key !== 'entity_type')
            ?? withOptions[0];
        const value = sample?.filters.find(f => f.value !== '__empty__')?.text;
        return queryHelpRows(sample && value ? `${sample.title} = "${value}"` : 'Type = "People"');
    }, [tableColumns]);
    const commitQueryDraft = () => {
        if (queryDraft === null) return;
        const text = queryDraft.trim();
        if (text !== querySummary.trim()) {
            // Typed words with no operator in them aren't a malformed query,
            // they're a search: the query bar is the one search box on this
            // page. It expands to both text clauses, OR'd: the
            // hybrid search ranks and caps its hits at 50, so a plain substring
            // in a name could otherwise fall off the end of it. Committing
            // rewrites the bar in full syntax, so the grammar shows itself.
            applyQuery(text && !isQueryExpression(text)
                ? buildBrowseQuery({
                    columns: tableColumns,
                    groups: [{ name: [text] }, { description: [text] }],
                    tabLabel,
                })
                : text);
        }
        setQueryDraft(null);
    };

    const copyQueryLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            // Say plainly that it travels, and that it carries the search
            // rather than the rows — what a recipient sees is still their own.
            message.success({
                content: 'Link copied — send it to anyone. It reopens this search for them, over whatever they can see.',
                duration: 5,
            });
        } catch {
            message.error("Couldn't copy the link");
        }
    };

    // "Clear" only ever undoes filters and sort: the tab and the column layout
    // are the viewer's own place in the app, not part of a query they ran.
    const hasFilters = hasFilterClauses(filterGroups);
    const hasSort = Object.keys(tableSorters).length > 0;
    const clearLabel = !hasFilters ? 'Clear sort' : !hasSort ? 'Clear filters' : 'Clear all';

    const clearFiltersAndSort = () => {
        setFilterGroups([{}]);
        setTableSorters({});
        setQueryDraft(null);
    };

    // Bulk action bar (superadmin, table view): derive from the current
    // selection which of "make admin" / "remove admin" / "delete" apply.
    // Admin toggling only makes sense for claimed people (they're the ones
    // with a Keycloak account behind them); delete works on any entity type.
    // Your own account is deleted from the Account page (password-confirmed,
    // with a last-admin warning) — never from a bulk selection, which could
    // sweep away every admin at once. The backend refuses it too. Your row
    // stays selectable for the other actions; only Delete leaves it out.
    const isOwnAccount = e => e.entity_type === 'person' && !!e.user_id && String(e.user_id) === String(user?.id);
    const selectedEntities = useMemo(
        () => filteredEntities.filter(e => selectedRowKeys.includes(e.id)),
        [filteredEntities, selectedRowKeys],
    );
    const deletableEntities = selectedEntities.filter(e => !isOwnAccount(e));
    const ownAccountSelected = deletableEntities.length < selectedEntities.length;
    const selectedClaimedPeople = useMemo(
        () => selectedEntities.filter(e => e.entity_type === 'person' && !!e.user_id),
        [selectedEntities],
    );
    const superAdminUserIds = useMemo(
        () => new Set(superAdminSet.map(a => String(a.user_id))),
        [superAdminSet],
    );
    const selectedNonAdminPeople = selectedClaimedPeople.filter(e => !superAdminUserIds.has(String(e.user_id)));
    const selectedAdminPeople = selectedClaimedPeople.filter(e => superAdminUserIds.has(String(e.user_id)));

    const runBulkAction = async (items, fn, successMsg, failMsg) => {
        setBulkActionBusy(true);
        const results = await Promise.allSettled(items.map(fn));
        setBulkActionBusy(false);
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed > 0) message.error(`${failMsg} (${failed}/${items.length} failed)`);
        else message.success(successMsg);
    };

    const handleBulkMakeAdmin = () => {
        runBulkAction(
            selectedNonAdminPeople,
            e => addSuperAdmin(e.user_id),
            `Made ${selectedNonAdminPeople.length} admin${selectedNonAdminPeople.length === 1 ? '' : 's'}`,
            'Could not make some people admin',
        ).then(() => { refreshSuperAdmins(); refreshAdminPeople(); setSelectedRowKeys([]); });
    };

    const handleBulkRemoveAdmin = () => {
        runBulkAction(
            selectedAdminPeople,
            e => removeSuperAdmin(e.user_id),
            `Removed admin from ${selectedAdminPeople.length} ${selectedAdminPeople.length === 1 ? 'person' : 'people'}`,
            'Could not remove admin from some people',
        ).then(() => { refreshSuperAdmins(); refreshAdminPeople(); setSelectedRowKeys([]); });
    };

    const handleBulkDelete = () => {
        return runBulkAction(
            deletableEntities,
            e => api.del(`/api/entities/${e.id}/with-account`, { params: { user_id: user?.id } }),
            `Deleted ${deletableEntities.length} ${deletableEntities.length === 1 ? 'entity' : 'entities'}`,
            'Could not delete some entities',
        ).then(() => {
            setBulkDeleteOpen(false);
            fetchEntities(); refreshUnclaimedPeople(); refreshAdminPeople(); setSelectedRowKeys([]);
        });
    };

    // Client-side from the selected rows. Entity.email is only set when an
    // admin enters it (or a CSV import does); people who signed up and claimed
    // their entity have their email on their user profile instead, so fill
    // those in from /api/users/{user_id}/profile before building the CSV.
    const [exportingCsv, setExportingCsv] = useState(false);
    // buildContactsCsv() output awaiting confirmation in ExportCsvModal.
    const [exportResult, setExportResult] = useState(null);
    const exportableCount = selectedEntities.filter(e => (e.email || '').trim() || e.user_id).length;
    const handleExportCsv = async () => {
        setExportingCsv(true);
        try {
            const withEmails = await Promise.all(selectedEntities.map(e => {
                if ((e.email || '').trim() || !e.user_id) return e;
                return api.get(`/api/users/${e.user_id}/profile`)
                    .then(profile => ({ ...e, email: profile?.email || '' }))
                    .catch(() => e);
            }));
            // Confirm in a modal: who's missing an email, and the file name.
            setExportResult(buildContactsCsv(withEmails, personFields));
        } finally {
            setExportingCsv(false);
        }
    };
    const handleConfirmExport = filename => {
        downloadCsv(exportResult.csv, filename);
        message.success(`Exported ${exportResult.exported} contact${exportResult.exported === 1 ? '' : 's'} to ${filename}`);
        setExportResult(null);
    };

    // The Unapproved tab always renders registration cards, whatever viewMode says.
    const columnsDisabled = viewMode !== 'table' || typeFilter === 'unapproved';

    return (
        <div className="browse-page" style={{ maxWidth: 1200, margin: '0 auto' }}>
            {liveEntities.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <FireOutlined style={{ color: '#ff4d4f' }} />
                        <Text strong style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Live now
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            ({liveEntities.length} active hangout{liveEntities.length === 1 ? '' : 's'})
                        </Text>
                    </div>
                    <Space wrap size="middle">
                        {liveEntities.map(e => (
                            <EntityCard key={e.id} entity={e} unclaimed={isUnclaimed(e)} unlisted={isUnlisted(e)} showTypeIdentifiers={featureEntityTypeIdentifiers} onClick={() => navigate(`/entity/${e.id}`)} />
                        ))}
                    </Space>
                </div>
            )}

            <div>
                <div className="browse-header-row">
                    <Space wrap size="middle">
                        <div className="browse-segmented-scroll">
                            <Segmented
                                value={typeFilter}
                                // The tab narrows in addition to whatever's already
                                // filtered — it's one more AND clause in the query,
                                // not a reset. Only the row selection is dropped,
                                // since those ids belong to the tab being left.
                                onChange={v => { setTypeFilter(v); setSelectedRowKeys([]); }}
                                options={typeFilters.map(t => ({
                                    value: t.value,
                                    label: (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                                            {t.icon}
                                            {t.label}
                                        </span>
                                    ),
                                }))}
                            />
                        </div>
                        <Segmented
                            value={viewMode}
                            disabled={typeFilter === 'unapproved'}
                            onChange={v => { setViewModePersisted(v); setSelectedRowKeys([]); }}
                            options={[
                                { value: 'table', icon: <TableOutlined /> },
                                { value: 'calendar', icon: <CalendarOutlined /> },
                            ]}
                        />
                        {/* Always rendered (disabled outside table view) so the
                            toolbar doesn't shift when switching views. */}
                        <Popover
                            open={columnsDisabled ? false : undefined}
                            trigger="click"
                            placement="bottomRight"
                            title="Columns"
                            content={(
                                <Space direction="vertical" size={4}>
                                    {tableColumns.map(col => (
                                        <Checkbox
                                            key={col.key}
                                            checked={isColumnVisible(col)}
                                            disabled={col.key === 'name'}
                                            onChange={e => setColumnVisible(col.key, e.target.checked)}
                                        >
                                            {col.title}
                                        </Checkbox>
                                    ))}
                                </Space>
                            )}
                        >
                            <Tooltip title={columnsDisabled ? 'Column settings apply to table view only' : 'Choose columns'}>
                                <Button icon={<SettingOutlined />} aria-label="Choose columns" disabled={columnsDisabled} />
                            </Tooltip>
                        </Popover>
                        {isServerAdmin && !personOnlyNode && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateModalOpen(true)}
                            >
                                Add Entity
                            </Button>
                        )}
                    </Space>
                    {/* Superadmin people actions, pushed to the end of the same
                        row as the tabs rather than taking a row of their own. */}
                    {isServerAdmin && (
                        <div className="browse-admin-actions">
                            <Button
                                icon={<ImportOutlined />}
                                onClick={() => setCsvImportOpen(true)}
                            >
                                Import CSV
                            </Button>
                            <Button
                                icon={<UserAddOutlined />}
                                onClick={() => setInviteOpen(true)}
                            >
                                Invite People
                            </Button>
                        </div>
                    )}
                </div>

                {typeFilter === 'unapproved' ? (
                    unapprovedPeople.length === 0 ? (
                        <Empty description={<Text type="secondary">No one is waiting for approval.</Text>} />
                    ) : (
                        <Space wrap size="middle">
                            {unapprovedPeople.map(r => (
                                <RegistrationCard
                                    key={r.id}
                                    registration={r}
                                    busy={registrationBusy === r.id}
                                    onApprove={() => handleRegistration(r.id, 'approved', approve)}
                                    onReject={() => handleRegistration(r.id, 'rejected', reject)}
                                />
                            ))}
                        </Space>
                    )
                ) : (typeFilter === 'admins' ? adminsLoading : loading) ? (
                    <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
                ) : (typeFilter !== 'admins' && loadError) ? (
                    <Empty description={<Text type="secondary">Couldn't load entities. Try refreshing.</Text>} />
                ) : filteredEntities.length === 0 ? (
                    <Empty description={<Text type="secondary">Nothing here yet for this filter.</Text>} />
                ) : (
                    <>
                        <div className="browse-filter-banner">
                            <Input.TextArea
                                className="browse-query-input"
                                variant="borderless"
                                spellCheck={false}
                                autoSize={{ minRows: 1, maxRows: 4 }}
                                value={queryDraft ?? querySummary}
                                onChange={e => setQueryDraft(e.target.value)}
                                onFocus={() => setQueryFocused(true)}
                                onBlur={() => { setQueryFocused(false); commitQueryDraft(); }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitQueryDraft(); }
                                    if (e.key === 'Escape') { setQueryDraft(null); setQueryFocused(false); e.currentTarget.blur(); }
                                }}
                                placeholder={'Search, or write a query — Description means "ali"  OR  Type = "Contexts"'}
                            />
                            <Space size={0}>
                                {descriptionSearching && <Spin size="small" style={{ marginRight: 8 }} />}
                                {queryDraft !== null && (
                                    <Button type="link" size="small" onClick={commitQueryDraft}>Apply</Button>
                                )}
                                {querySummary && (
                                    <Tooltip title="Copy a link to this search — filters, sort, tab and columns. Share it with anyone: they get the same search, run against what they're allowed to see.">
                                        <Button type="link" size="small" icon={<LinkOutlined />} onClick={copyQueryLink}>Copy link</Button>
                                    </Tooltip>
                                )}
                                {(hasFilters || hasSort) && (
                                    <Button type="link" size="small" onClick={clearFiltersAndSort}>{clearLabel}</Button>
                                )}
                            </Space>
                        </div>
                        {(queryFocused || queryDraft !== null) && (
                            <div className="browse-query-help">
                                {queryHelp.map(([syntax, what]) => (
                                    <React.Fragment key={syntax}>
                                        <code>{syntax}</code>
                                        <Text type="secondary">{what}</Text>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                        {viewMode === 'calendar' ? (
                            <BrowseCalendar
                                entities={visibleEntities}
                                onOpen={e => navigate(`/entity/${e.id}`)}
                                showTypeIdentifiers={featureEntityTypeIdentifiers}
                                typeColors={ENTITY_TYPE_COLORS}
                            />
                        ) : (
                            <>
                                {isServerAdmin && selectedRowKeys.length > 0 && (
                                    <div className="browse-bulk-action-bar">
                                        <Text type="secondary">{selectedRowKeys.length} selected</Text>
                                        <Space size={8} wrap>
                                            <Tooltip title={exportableCount === 0 ? 'No selected entities have an email' : 'Export selected contacts as CSV'}>
                                                <Button
                                                    icon={<DownloadOutlined />}
                                                    disabled={exportableCount === 0}
                                                    loading={exportingCsv}
                                                    onClick={handleExportCsv}
                                                >
                                                    Export CSV{exportableCount > 0 ? ` (${exportableCount})` : ''}
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title={selectedNonAdminPeople.length === 0 ? 'No claimed people selected' : ''}>
                                                <Button
                                                    icon={<SafetyCertificateOutlined />}
                                                    disabled={selectedNonAdminPeople.length === 0}
                                                    loading={bulkActionBusy}
                                                    onClick={handleBulkMakeAdmin}
                                                >
                                                    Make admin{selectedNonAdminPeople.length > 0 ? ` (${selectedNonAdminPeople.length})` : ''}
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title={selectedAdminPeople.length === 0 ? 'No admins selected' : ''}>
                                                <Button
                                                    icon={<SafetyCertificateOutlined />}
                                                    disabled={selectedAdminPeople.length === 0}
                                                    loading={bulkActionBusy}
                                                    onClick={handleBulkRemoveAdmin}
                                                >
                                                    Remove admin{selectedAdminPeople.length > 0 ? ` (${selectedAdminPeople.length})` : ''}
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title={ownAccountSelected ? 'Your own account is skipped — delete it from your Account page' : ''}>
                                                <Button
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    disabled={deletableEntities.length === 0}
                                                    loading={bulkActionBusy}
                                                    onClick={() => setBulkDeleteOpen(true)}
                                                >
                                                    Delete ({deletableEntities.length})
                                                </Button>
                                            </Tooltip>
                                        </Space>
                                    </div>
                                )}
                                {/* Scrolls sideways once dragged columns add up to more
                                    than the page is wide, instead of the whole page.
                                    overflow-x: auto silently turns overflow-y to auto
                                    too; the page already scrolls vertically, so a few
                                    px of spill mustn't get a second scrollbar here. */}
                                <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                                <Table
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    components={TABLE_COMPONENTS}
                                    columns={visibleColumns}
                                    onChange={(_, filters, sorter) => { applyDropdownFilters(filters); applySorter(sorter); }}
                                    dataSource={visibleEntities}
                                    rowSelection={isServerAdmin ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
                                    onRow={(entity) => ({
                                        onClick: (e) => {
                                            // The checkbox column is for selecting, not opening — a
                                            // click that misses the box itself shouldn't navigate.
                                            if (e.target.closest('.ant-table-selection-column')) return;
                                            navigate(`/entity/${entity.id}`);
                                        },
                                        style: { cursor: 'pointer' },
                                    })}
                                />
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {isServerAdmin && !personOnlyNode && (
                <CreateEntityModal
                    open={createModalOpen}
                    onCancel={() => setCreateModalOpen(false)}
                    onSuccess={() => { setCreateModalOpen(false); fetchEntities(); }}
                    showListedToggle={featureEntityListing}
                />
            )}

            {isServerAdmin && (
                <BulkDeleteModal
                    open={bulkDeleteOpen}
                    entities={deletableEntities}
                    busy={bulkActionBusy}
                    onCancel={() => setBulkDeleteOpen(false)}
                    onConfirm={handleBulkDelete}
                />
            )}

            <ExportCsvModal
                open={!!exportResult}
                result={exportResult}
                onCancel={() => setExportResult(null)}
                onExport={handleConfirmExport}
            />

            {isServerAdmin && (
                <CSVImportModal
                    open={csvImportOpen}
                    onCancel={() => setCsvImportOpen(false)}
                    onSuccess={fetchEntities}
                />
            )}

            {isServerAdmin && (
                <InvitePeopleModal
                    open={inviteOpen}
                    onCancel={() => setInviteOpen(false)}
                    onSuccess={refreshUnclaimedPeople}
                />
            )}
        </div>
    );
}

export default Homepage;
